/**
 * CalendarAliasRow — typed variant for `template="calendar"` alias
 * rows. Replaces the deleted `CalendarRenderer` and ad-hoc
 * `findCalendarAttrs` helper with a proper subclass dispatched at
 * tree-construction time by `registry.ts`.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import { type TimeOfDay, type Tzname } from "@tento-chrono";
import type { AliasDescriptor } from "../descriptor.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import type { ModelRowOptions } from "../row-options.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import { AliasRow } from "./alias-row.ts";
import { TimedGridConfig } from "./timed-grid-config.ts";

/**
 * An alias row that lays out a calendar: its timed grid and the calendars it
 * shows.
 */
export class CalendarAliasRow extends AliasRow {
  static readonly MARGIN_TOP = 12;
  static readonly MARGIN_BOTTOM = 12;

  declare readonly descriptor: AliasDescriptor;

  constructor(id: RowLocalId, descriptor: AliasDescriptor, sheet: ModelSheet) {
    super(id, descriptor, sheet);
  }

  /**
   * Timed-grid config from this alias's attribute bag. Delegates to the shared
   * row-agnostic helper so the alias and the `calendar-body` layout it composes
   * compute identical config from one source of truth.
   */
  #timedConfig(): TimedGridConfig {
    return TimedGridConfig.fromAttributes(
      TimedGridConfig.withChildOverride(
        this.attributes() ?? {},
        TimedGridConfig.BODY_PRESET,
      ),
    );
  }

  /** Visible day window (startHr–endHr from the alias attribute bag). */
  hoursRange(): TimeOfDay.Range {
    return this.#timedConfig().hoursRange();
  }

  get calendarType(): "default" | "list" | "custom" {
    return this.#timedConfig().calendarType;
  }

  get timezone(): Option<Tzname> {
    return this.#timedConfig().timezone;
  }

  get disableMonthlyView(): boolean {
    return this.#timedConfig().disableMonthlyView;
  }

  get hideAllDayEvents(): boolean {
    return Json.boolean(this.attributes(), "hideAllDayEvents") ?? false;
  }

  effectiveView(sheetTimescale: string): "daily" | "monthly" {
    return this.#timedConfig().effectiveView(sheetTimescale);
  }

  /**
   * Options the calendar surfaces on its own visible row. The timed-grid
   * config (Hours / monthly-view / style / timezone) writes to the body's
   * per-child OVERRIDE slot `attributes.children["calendar-body"]` via
   * `alias-child-attr` targets — the same cascade `timedConfig` reads (slot →
   * legacy top-level → row-type default), so the composed surface and these
   * getters stay in sync and legacy top-level config keeps working with no
   * migration. The all-day strip stays a top-level alias attr (it gates the
   * all-day projection child, not the timed grid). Hours + monthly-view are
   * `primary`; style/all-day group under "Display"; timezone under "Time".
   */
  override optionContributions(): readonly ModelRowOptions.Contribution[] {
    const rowId = this.id;
    return [
      {
        key: "calendar:hours",
        label: "Hours",
        tier: "primary",
        priority: 100,
        section: null,
        target: {
          kind: "alias-child-attr-range",
          rowId,
          preset: TimedGridConfig.BODY_PRESET,
          startAttr: "startHr",
          endAttr: "endHr",
        },
        control: { kind: "time-range", min: 0, max: 24 },
        // The hour window resizes the day columns — recompute layout.
        reRender: "layout",
      },
      {
        key: "calendar:monthly-view",
        label: "Monthly view",
        tier: "primary",
        priority: 90,
        section: null,
        // `disableMonthlyView` is inverted at the control (toggle ON =
        // monthly enabled); the write target is the stored attr.
        target: {
          kind: "alias-child-attr",
          rowId,
          preset: TimedGridConfig.BODY_PRESET,
          attr: "disableMonthlyView",
        },
        control: { kind: "toggle", invert: true },
        reRender: "layout",
      },
      {
        key: "calendar:display-type",
        label: "Calendar style",
        tier: "section",
        priority: 60,
        section: "Display",
        target: {
          kind: "alias-child-attr",
          rowId,
          preset: TimedGridConfig.BODY_PRESET,
          attr: "type",
        },
        control: {
          kind: "select",
          options: [
            { value: "default", label: "Default" },
            { value: "list", label: "List" },
          ],
        },
        reRender: "layout",
      },
      {
        key: "calendar:all-day",
        label: "All-day strip",
        tier: "section",
        priority: 50,
        section: "Display",
        // Shown = `!hideAllDayEvents`.
        target: { kind: "alias-attr", rowId, attr: "hideAllDayEvents" },
        control: { kind: "toggle", invert: true },
        // Toggles the all-day projection child — rebuild the structure.
        reRender: "structure",
      },
      {
        key: "calendar:timezone",
        label: "Time zone",
        tier: "section",
        priority: 40,
        section: "Time",
        target: {
          kind: "alias-child-attr",
          rowId,
          preset: TimedGridConfig.BODY_PRESET,
          attr: "timezone",
        },
        control: { kind: "timezone" },
        reRender: "layout",
      },
    ];
  }
}
